// Client side of the tile front door (vortex-tiles, tiles.vortexnav.com): the tile
// address a map layer uses, carrying the tile pass vortex-api issued.
//
// @docs:
//   - docs/planning/TILE_FRONT_DOOR_PLAN.md §3.1
//
// Framework-neutral, like cdn.ts: Leaflet and MapLibre both take a `{z}/{x}/{y}`
// template. The pass rides in the query string because an <img> tile cannot send
// a header; it is short-lived and opens only the sources it names.

import { providerForSource } from './providers';

export const TILES_URL = 'https://tiles.vortexnav.com';

/**
 * A `{z}/{x}/{y}` template for a source on the front door. `options` are the source's
 * own choices (Siitech's `c`, `w` …); `mode` says whether the tile is viewed or
 * downloaded for offline use, which the front door records with each tile.
 */
export function frontDoorTemplate(
  sourceId: string,
  pass: string,
  options: Record<string, string> = {},
  baseUrl = TILES_URL,
  mode: 'live' | 'download' = 'live',
): string {
  const provider = providerForSource(sourceId);
  if (!provider) throw new Error(`Unknown front-door source: ${sourceId}`);
  const query = new URLSearchParams({ pass, ...options });
  if (mode === 'download') query.set('mode', 'download');
  return `${baseUrl.replace(/\/$/, '')}/v1/${provider.id}/{z}/{x}/{y}.${provider.extension}?${query.toString()}`;
}

/**
 * The fallback address for one tile that failed on the front door. Leaflet reports a failed
 * tile by its unwrapped position, so a tile past 180° carries an x outside the world;
 * wrap it, as the layer itself did, before filling the template.
 */
export function fallbackTileUrl(template: string, coords: { x: number; y: number; z: number }, subdomains = 'abc'): string {
  const n = 2 ** coords.z;
  const x = ((coords.x % n) + n) % n;
  const s = subdomains[(x + coords.y) % subdomains.length];
  return template.replace(/\{s\}/g, s).replace(/\{z\}/g, String(coords.z)).replace(/\{x\}/g, String(x)).replace(/\{y\}/g, String(coords.y));
}
